"use client";

import { ChevronLeft, ChevronRight, FilterX, Hand, Loader2, Mic, PhoneIncoming, PhoneOutgoing, Search, Users, Zap } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { RecordingPlayer, fmtClock } from "@/app/_module/components/RecordingPlayer";
import type { CallRecording } from "@/app/_module/contexts/CallRecordingContext";
import { CallTranscriptPanel } from "@/app/(authenticated)/leads/_modules/components/CallTranscriptPanel";
import { stagePill } from "./AgentPerformanceInsights";

/**
 * Call Recordings card on Agent Details — every call is recorded, so the
 * owner can search a lead and listen in place, via the shared RecordingPlayer
 * (lazy blob load, seek, speed cycling, live clock — primary-token themed).
 */

interface RecordingRow {
  id: string;
  call_date: string;
  call_type?: string;
  duration?: number;
  recording_duration?: number;
  recording_url: string;
  phone_number?: string;
  contact_name?: string;
  lead_id?: string;
  lead_name?: string;
  lead_status?: string;
  lead_state?: string;
  dialer_mode?: "power_dialer" | "multi_line" | "manual";
}

const fmtDateTime = (d?: string | null) =>
  d
    ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";

// A row as the app-wide player's recording, so it keeps playing after the owner leaves this page.
const toRecording = (r: RecordingRow): CallRecording => ({
  callId: r.id,
  recordingUrl: r.recording_url,
  title: r.lead_name || r.contact_name || formatPhoneNumber(r.phone_number || "") || "Unknown caller",
  direction: /in(coming|bound)/i.test(r.call_type || "") ? "inbound" : "outbound",
  callDate: r.call_date,
  durationSecs: Number(r.recording_duration || r.duration) || 0,
  leadId: r.lead_id,
});

export function AgentCallRecordings({ agencyId, agentId }: { agencyId: string; agentId: string }) {
  const ALL = "__all__";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [direction, setDirection] = useState<string | undefined>();
  const [mode, setMode] = useState<string | undefined>();
  const [minDuration, setMinDuration] = useState<string | undefined>();
  const hasFilters = !!(direction || mode || minDuration);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [debounced, agentId, direction, mode, minDuration]);

  const { data, isLoading, isFetching } = useQuery<{ recordings: RecordingRow[]; total: number; totalPages: number }>({
    queryKey: ["agent-recordings", agencyId, agentId, debounced, page, direction, mode, minDuration],
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(page) });
      if (debounced) qs.set("search", debounced);
      if (direction) qs.set("direction", direction);
      if (mode) qs.set("mode", mode);
      if (minDuration) qs.set("minDuration", minDuration);
      const res: any = await handleApiCall(async () =>
        api.get({ url: getApiUrl(`/agencies/${agencyId}/agents/${agentId}/recordings?${qs}`), auth: true })
      );
      const d = res?.recordings ? res : res?.data;
      return { recordings: d?.recordings || [], total: Number(d?.total) || 0, totalPages: Number(d?.totalPages) || 1 };
    },
    enabled: !!agencyId && !!agentId,
    staleTime: 60 * 1000,
    placeholderData: (prev: any) => prev,
  });

  const rows = data?.recordings || [];

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Mic className="h-4 w-4 text-primary" />
          Call Recordings
          {data != null && <span className="text-sm font-normal text-gray-400">· {data.total.toLocaleString()}</span>}
        </CardTitle>
        <CardDescription className="text-xs">Every call is recorded — search a lead and listen</CardDescription>
        <div className="relative mt-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lead name or number…"
            className="h-8 pl-8 text-sm"
          />
        </div>
        {/* server-side filters */}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Select value={direction ?? ALL} onValueChange={(v) => setDirection(v === ALL ? undefined : v)}>
            <SelectTrigger className="h-7 w-auto min-w-[104px] text-xs"><SelectValue placeholder="Direction" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All directions</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
            </SelectContent>
          </Select>
          <Select value={mode ?? ALL} onValueChange={(v) => setMode(v === ALL ? undefined : v)}>
            <SelectTrigger className="h-7 w-auto min-w-[104px] text-xs"><SelectValue placeholder="Dialer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All dialers</SelectItem>
              <SelectItem value="power_dialer">Power Dialer</SelectItem>
              <SelectItem value="multi_line">Multi-Line</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={minDuration ?? ALL} onValueChange={(v) => setMinDuration(v === ALL ? undefined : v)}>
            <SelectTrigger className="h-7 w-auto min-w-[96px] text-xs"><SelectValue placeholder="Length" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any length</SelectItem>
              <SelectItem value="30">30s+</SelectItem>
              <SelectItem value="60">1min+</SelectItem>
              <SelectItem value="300">5min+</SelectItem>
              <SelectItem value="600">10min+</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <button
              type="button"
              onClick={() => { setDirection(undefined); setMode(undefined); setMinDuration(undefined); setPage(1); }}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <FilterX className="h-3 w-3" /> Clear
            </button>
          )}
          {/* pagination lives up here so you never scroll to page */}
          <span className="ml-auto flex items-center gap-1.5">
            <span className="text-[11px] tabular-nums text-gray-400">
              {page}/{data?.totalPages || 1}
              {isFetching && <Loader2 className="ml-1 inline h-3 w-3 animate-spin" />}
            </span>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={isFetching || page >= (data?.totalPages || 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            {debounced || hasFilters ? "No recordings match these filters" : "No recordings yet"}
          </p>
        ) : (
            <div className="max-h-[560px] flex-1 space-y-2.5 overflow-y-auto pr-1 lg:max-h-none lg:min-h-0">
              {rows.map((r) => {
                const name = r.lead_name || r.contact_name || (r.phone_number ? formatPhoneNumber(r.phone_number) : "Unknown");
                const inbound = /in(coming|bound)/i.test(r.call_type || "");
                return (
                  <div key={r.id} className="rounded-xl border border-gray-100 bg-gradient-to-b from-white to-gray-50/70 p-3 shadow-sm transition-shadow hover:shadow">
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${inbound ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}>
                          {inbound ? <PhoneIncoming className="h-3.5 w-3.5" /> : <PhoneOutgoing className="h-3.5 w-3.5" />}
                        </span>
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-gray-900">
                            {name}
                            {r.lead_status && (
                              <span className={`shrink-0 rounded-full px-1.5 py-px text-[9px] font-semibold ${stagePill(r.lead_status)}`}>{r.lead_status}</span>
                            )}
                          </p>
                          <p className="truncate text-[11px] text-gray-500">
                            {r.phone_number ? formatPhoneNumber(r.phone_number) : "—"}
                            {r.lead_state && <span className="text-gray-400"> · {r.lead_state}</span>}
                            <span className="text-gray-400"> · {inbound ? "Inbound" : "Outbound"}</span>
                          </p>
                          {r.dialer_mode && (
                            <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9px] font-semibold ring-1 ring-inset ${
                              r.dialer_mode === "power_dialer" ? "bg-violet-50 text-violet-700 ring-violet-600/20"
                              : r.dialer_mode === "multi_line" ? "bg-blue-50 text-blue-700 ring-blue-600/20"
                              : "bg-gray-100 text-gray-600 ring-gray-500/20"
                            }`}>
                              {r.dialer_mode === "power_dialer" ? <Zap className="h-2.5 w-2.5" /> : r.dialer_mode === "multi_line" ? <Users className="h-2.5 w-2.5" /> : <Hand className="h-2.5 w-2.5" />}
                              {r.dialer_mode === "power_dialer" ? "Power Dialer" : r.dialer_mode === "multi_line" ? "Multi-Line" : "Manual"}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 text-right text-[10px] leading-4 text-gray-400">
                        {fmtDateTime(r.call_date)}
                        <br />
                        <span className="tabular-nums">{fmtClock(Number(r.recording_duration || r.duration) || 0)} call</span>
                      </span>
                    </div>
                    <RecordingPlayer
                      recording={toRecording(r)}
                      downloadName={`${(r.lead_name || r.contact_name || "recording").replace(/\s+/g, "_")}_${r.call_date?.slice(0, 10) || ""}.mp3`}
                    />
                    <CallTranscriptPanel recording={toRecording(r)} />
                  </div>
                );
              })}
            </div>
        )}
      </CardContent>
    </Card>
  );
}
